import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Heart, ZoomIn, Maximize2 } from 'lucide-react';
import './CarDetailsModal.css';

/**
 * Улучшенное модальное окно для отображения подробной информации об автомобиле
 */
const FullscreenGallery = ({ photos, initialIndex, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [thumbnailsVisible, setThumbnailsVisible] = useState(true);

    // Обработка нажатия клавиш для навигации
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                handlePrevImage();
            } else if (e.key === 'ArrowRight') {
                handleNextImage();
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex]);

    // Навигация по фотографиям
    const handlePrevImage = () => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
    };

    const handleNextImage = () => {
        setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
    };

    // Обработка клика на миниатюру
    const handleThumbnailClick = (index) => {
        setCurrentIndex(index);
    };

    // Переключение видимости миниатюр
    const toggleThumbnails = () => {
        setThumbnailsVisible(!thumbnailsVisible);
    };

    return (
        <div className="fullscreen-gallery-overlay" onClick={onClose}>
            {/* Контейнер галереи, останавливаем всплытие кликов */}
            <div className="fullscreen-gallery-container" onClick={(e) => e.stopPropagation()}>
                {/* Заголовок с кнопкой закрытия */}
                <div className="fullscreen-gallery-header">
                    <div className="fullscreen-gallery-counter">
                        Фото {currentIndex + 1} из {photos.length}
                    </div>
                    <button className="fullscreen-gallery-close" onClick={onClose}>
                        <X size={24} />
                    </button>
                </div>

                {/* Основное изображение */}
                <div className="fullscreen-gallery-content">
                    <img
                        src={photos[currentIndex]}
                        alt={`Фото ${currentIndex + 1}`}
                        className="fullscreen-gallery-image"
                    />

                    {/* Навигационные кнопки */}
                    <button
                        className="fullscreen-gallery-nav prev-button"
                        onClick={handlePrevImage}
                        aria-label="Предыдущее фото"
                    >
                        <ChevronLeft size={30} />
                    </button>

                    <button
                        className="fullscreen-gallery-nav next-button"
                        onClick={handleNextImage}
                        aria-label="Следующее фото"
                    >
                        <ChevronRight size={30} />
                    </button>
                </div>

                {/* Миниатюры */}
                {thumbnailsVisible && (
                    <div className="fullscreen-gallery-thumbnails">
                        {photos.map((photo, index) => (
                            <div
                                key={index}
                                className={`fullscreen-thumbnail ${index === currentIndex ? 'active' : ''}`}
                                onClick={() => handleThumbnailClick(index)}
                            >
                                <img src={photo} alt={`Миниатюра ${index + 1}`} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const NewCarDetailsModal = ({ car, onClose, onOpenApplicationModal }) => {
    // Состояния
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [photoUrls, setPhotoUrls] = useState([]);
    const [isFavorite, setIsFavorite] = useState(false);
    const [isFullscreenMode, setIsFullscreenMode] = useState(false);

    // Форматирование цены - ИЗМЕНЕНО: округляем до целого и убираем десятичные знаки
    const formatPrice = (price) => {
        if (!price) return '₽0';
        return `₽${Math.round(price).toLocaleString()}`;
    };

    // Обработка фотографий при монтировании компонента
    useEffect(() => {
        if (!car) return;

        let imagesToShow = [];

        // Проверяем наличие массива изображений
        if (car.images && Array.isArray(car.images) && car.images.length > 0) {
            imagesToShow = car.images;
        }
        // Проверяем наличие одиночного изображения
        else if (car.image && typeof car.image === 'string') {
            imagesToShow = [car.image];
        }
        // Используем заглушку, если нет изображений
        else {
            imagesToShow = ['https://via.placeholder.com/400x300?text=Нет+изображения'];
        }

        setPhotoUrls(imagesToShow);
    }, [car]);

    // Навигация по фотографиям
    const handlePrevImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : photoUrls.length - 1));
    };

    const handleNextImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex(prev => (prev < photoUrls.length - 1 ? prev + 1 : 0));
    };

    // Добавление/удаление из избранного
    const toggleFavorite = (e) => {
        e.stopPropagation();
        setIsFavorite(!isFavorite);
        // Здесь можно добавить логику сохранения в избранное
    };

    // Открытие полноэкранного режима просмотра
    const openFullscreenGallery = (e) => {
        e.stopPropagation();
        setIsFullscreenMode(true);
        // Блокируем прокрутку страницы при открытии полноэкранной галереи
        document.body.style.overflow = 'hidden';
    };

    // Закрытие полноэкранного режима просмотра
    const closeFullscreenGallery = () => {
        setIsFullscreenMode(false);
        // Возвращаем прокрутку страницы при закрытии полноэкранной галереи
        document.body.style.overflow = 'auto';
    };

    // Не рендерим, если нет данных
    if (!car) return null;

    return (
        <div className="modal-overlay-improved">
            <div className="car-details-modal-improved">
                {/* Заголовок */}
                <div className="modal-header-improved">
                    <h3 className="modal-title-improved">{car.brand} {car.model}</h3>
                    <button className="modal-close-improved" onClick={onClose}>
                        <X size={24} />
                    </button>
                </div>

                {/* Основной контент */}
                <div className="modal-content-improved">
                    {/* Левая колонка с фотографией */}
                    <div className="car-gallery-column">
                        <div className="car-main-image-wrapper">
                            {/* Основное изображение с возможностью клика для полноэкранного просмотра */}
                            <div
                                className="car-gallery-main-improved"
                                onClick={openFullscreenGallery}
                            >
                                <img
                                    src={photoUrls[currentImageIndex] || 'https://via.placeholder.com/400x300?text=Нет+изображения'}
                                    alt={`${car.brand} ${car.model}`}
                                    className="car-gallery-main-image"
                                />

                                {/* Кнопка увеличения */}
                                <button
                                    className="fullscreen-button"
                                    onClick={openFullscreenGallery}
                                    aria-label="Посмотреть в полный экран"
                                >
                                    <Maximize2 size={20} />
                                </button>

                                {/* Навигационные кнопки */}
                                {photoUrls.length > 1 && (
                                    <>
                                        <button
                                            onClick={handlePrevImage}
                                            className="gallery-nav-button-improved prev-button"
                                        >
                                            <ChevronLeft size={24} />
                                        </button>
                                        <button
                                            onClick={handleNextImage}
                                            className="gallery-nav-button-improved next-button"
                                        >
                                            <ChevronRight size={24} />
                                        </button>
                                    </>
                                )}

                                {/* Кнопка избранного */}
                                <button
                                    className={`favorite-button ${isFavorite ? 'active' : ''}`}
                                    onClick={toggleFavorite}
                                >
                                    <Heart
                                        size={20}
                                        fill={isFavorite ? "#ef4444" : "none"}
                                        color={isFavorite ? "#ef4444" : "white"}
                                    />
                                </button>

                                {/* Счетчик фото */}
                                {photoUrls.length > 1 && (
                                    <div className="gallery-counter-improved">
                                        {currentImageIndex + 1} / {photoUrls.length}
                                    </div>
                                )}
                            </div>

                            {/* Миниатюры фото */}
                            {photoUrls.length > 1 && (
                                <div className="gallery-thumbnails-improved">
                                    {photoUrls.map((url, index) => (
                                        <div
                                            key={index}
                                            onClick={() => setCurrentImageIndex(index)}
                                            className={`gallery-thumbnail-improved ${index === currentImageIndex ? 'active' : ''}`}
                                        >
                                            <img
                                                src={url}
                                                alt={`Миниатюра ${index + 1}`}
                                                className="thumbnail-image"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Цена и год выпуска */}
                        <div className="car-price-year-improved">
                            <div className="car-price-improved">
                                <span className="car-price-value-improved">{formatPrice(car.price)}</span>
                            </div>
                            <div className="car-year-improved">
                                <span className="car-year-value">{car.year}</span>
                                <span className="car-year-label"> г.</span>
                            </div>
                        </div>
                    </div>

                    {/* Правая колонка с характеристиками */}
                    <div className="car-info-column">
                        {/* Основная информация */}
                        <div className="info-section">
                            <h4 className="section-title">Основная информация</h4>
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Марка:</span>
                                    <span className="info-value">{car.brand}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Модель:</span>
                                    <span className="info-value">{car.model}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Год выпуска:</span>
                                    <span className="info-value">{car.year}</span>
                                </div>
                            </div>
                        </div>

                        {/* Технические характеристики */}
                        <div className="info-section">
                            <h4 className="section-title">Технические характеристики</h4>
                            <div className="info-grid">
                                {car.engineVolume && (
                                    <div className="info-item">
                                        <span className="info-label">Объем двигателя:</span>
                                        <span className="info-value">
                                            {car.engineVolume > 0 ? `${car.engineVolume} л` : 'Электро'}
                                        </span>
                                    </div>
                                )}
                                {car.color && (
                                    <div className="info-item">
                                        <span className="info-label">Цвет:</span>
                                        <span className="info-value">{car.color}</span>
                                    </div>
                                )}
                                {car.transmission && (
                                    <div className="info-item">
                                        <span className="info-label">Трансмиссия:</span>
                                        <span className="info-value">{car.transmission}</span>
                                    </div>
                                )}
                                {car.fuelType && (
                                    <div className="info-item">
                                        <span className="info-label">Вид топлива:</span>
                                        <span className="info-value">{car.fuelType}</span>
                                    </div>
                                )}                                {car.mileage != null && (
                                    <div className="info-item">
                                        <span className="info-label">Пробег:</span>
                                        <span className="info-value">{car.mileage.toLocaleString()} км</span>
                                    </div>
                                )}
                                {car.driveType && (
                                    <div className="info-item">
                                        <span className="info-label">Привод:</span>
                                        <span className="info-value">{car.driveType}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Описание */}
                        {(car.description || true) && (
                            <div className="info-section">
                                <h4 className="section-title">Описание</h4>
                                <p className="car-description-improved">
                                    {car.description || 'Автомобиль в ДТП НЕ участвовал • Без подкрасов и нюансов • Один владелец'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Кнопки действий */}
                <div className="modal-footer-improved">
                    <button
                        onClick={() => {
                            onClose();
                            onOpenApplicationModal(car);
                        }}
                        className="button-primary-improved"
                    >
                        Оставить заявку
                    </button>
                    <button
                        onClick={onClose}
                        className="button-secondary-improved"
                    >
                        Вернуться к списку
                    </button>
                </div>
            </div>

            {/* Полноэкранная галерея, показывается только когда isFullscreenMode = true */}
            {isFullscreenMode && (
                <FullscreenGallery
                    photos={photoUrls}
                    initialIndex={currentImageIndex}
                    onClose={closeFullscreenGallery}
                />
            )}
        </div>
    );
};

export default NewCarDetailsModal;